import { Fragment, type ReactNode } from "react";

const ALLOWED_PROTOCOLS = ["http:", "https:", "mailto:"];

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|_([^_]+)_|\*([^*]+)\*/g;
  let lastIndex = 0;
  let index = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const key = `${keyPrefix}-${index}`;
    if (match[1] !== undefined) {
      nodes.push(<strong key={key}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      nodes.push(<code key={key}>{match[2]}</code>);
    } else if (match[3] !== undefined && match[4] !== undefined) {
      const href = match[4];
      nodes.push(
        isSafeUrl(href)
          ? <a key={key} href={href} target="_blank" rel="noreferrer">{match[3]}</a>
          : match[3]
      );
    } else if (match[5] !== undefined) {
      nodes.push(<em key={key}>{match[5]}</em>);
    } else if (match[6] !== undefined) {
      nodes.push(<em key={key}>{match[6]}</em>);
    }
    lastIndex = pattern.lastIndex;
    index += 1;
    match = pattern.exec(text);
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function MarkdownLite({ content }: { content: string }) {
  const lines = content.split("\n");
  const blocks: ReactNode[] = [];
  let listBuffer: { ordered: boolean; items: string[] } | null = null;
  let blockIndex = 0;

  const flushList = () => {
    if (!listBuffer) return;
    const items = listBuffer.items;
    blocks.push(
      listBuffer.ordered ? (
        <ol key={`list-${blockIndex}`} className="markdown-lite-list">
          {items.map((item, i) => <li key={i}>{renderInline(item, `list-${blockIndex}-${i}`)}</li>)}
        </ol>
      ) : (
        <ul key={`list-${blockIndex}`} className="markdown-lite-list">
          {items.map((item, i) => <li key={i}>{renderInline(item, `list-${blockIndex}-${i}`)}</li>)}
        </ul>
      )
    );
    blockIndex += 1;
    listBuffer = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushList();
      continue;
    }
    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(line);
    const orderedMatch = /^\d+\.\s+(.*)$/.exec(line);
    const bulletMatch = /^[-*]\s+(.*)$/.exec(line);

    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const text = renderInline(headingMatch[2], `h-${blockIndex}`);
      blocks.push(
        level === 1
          ? <h3 key={`h-${blockIndex}`}>{text}</h3>
          : level === 2
            ? <h4 key={`h-${blockIndex}`}>{text}</h4>
            : <h5 key={`h-${blockIndex}`}>{text}</h5>
      );
      blockIndex += 1;
      continue;
    }
    if (orderedMatch) {
      if (!listBuffer || !listBuffer.ordered) {
        flushList();
        listBuffer = { ordered: true, items: [] };
      }
      listBuffer.items.push(orderedMatch[1]);
      continue;
    }
    if (bulletMatch) {
      if (!listBuffer || listBuffer.ordered) {
        flushList();
        listBuffer = { ordered: false, items: [] };
      }
      listBuffer.items.push(bulletMatch[1]);
      continue;
    }
    flushList();
    blocks.push(<p key={`p-${blockIndex}`}>{renderInline(line, `p-${blockIndex}`)}</p>);
    blockIndex += 1;
  }
  flushList();

  return <Fragment>{blocks}</Fragment>;
}
